/**
 * Timeline ruler component (Task 4.3, enhanced in Task Group 1)
 *
 * Displays time markers along the top of the timeline:
 * - Major markers every 1-2 seconds with time labels (depending on zoom)
 * - Minor markers every 0.1-0.5 seconds for grid alignment (depending on zoom)
 * - Width scales with timeline duration and zoom level
 *
 * The ruler provides visual feedback for the snap-to-grid system
 * and helps users align tools precisely on the timeline.
 *
 * Zoom-aware tick spacing (Task Group 1):
 * - At low zoom (50px/s): show major ticks every 2s, minor every 0.5s
 * - At default zoom (100px/s): show major ticks every 1s, minor every 0.1s
 * - At high zoom (300px/s): show major ticks every 1s, minor every 0.1s
 */

import { useMemo } from 'react';
import { useTimelineStore } from '../../stores/timelineStore';

interface TimelineRulerProps {
  /**
   * Pixels per second for time-to-pixel conversion
   * Default: 100px/s
   */
  pixelsPerSecond?: number;
}

/**
 * Determine tick spacing based on zoom level for readability
 * Returns { majorInterval, minorInterval } in seconds
 *
 * Adapts to zoom level to keep ~50-150px between major ticks
 */
function getTickSpacing(pixelsPerSecond: number): {
  majorInterval: number;
  minorInterval: number;
} {
  // Calculate ideal interval to maintain ~80px between major ticks
  const targetPixelsBetweenMajor = 80;
  const idealInterval = targetPixelsBetweenMajor / pixelsPerSecond;

  // Snap to nice round intervals
  if (idealInterval >= 600) {
    // 10+ minutes between ticks -> use 10 minute intervals
    return { majorInterval: 600, minorInterval: 60 };
  } else if (idealInterval >= 300) {
    // 5-10 minutes -> use 5 minute intervals
    return { majorInterval: 300, minorInterval: 60 };
  } else if (idealInterval >= 60) {
    // 1-5 minutes -> use 1 minute intervals
    return { majorInterval: 60, minorInterval: 10 };
  } else if (idealInterval >= 30) {
    // 30s-1min -> use 30 second intervals
    return { majorInterval: 30, minorInterval: 5 };
  } else if (idealInterval >= 10) {
    // 10-30s -> use 10 second intervals
    return { majorInterval: 10, minorInterval: 2 };
  } else if (idealInterval >= 5) {
    // 5-10s -> use 5 second intervals
    return { majorInterval: 5, minorInterval: 1 };
  } else if (idealInterval >= 2) {
    // 2-5s -> use 2 second intervals
    return { majorInterval: 2, minorInterval: 0.5 };
  } else if (idealInterval >= 1) {
    // 1-2s -> use 1 second intervals
    return { majorInterval: 1, minorInterval: 0.2 };
  }
  // < 1s -> use 0.5 second intervals (high zoom)
  return { majorInterval: 0.5, minorInterval: 0.1 };
}

/**
 * Generate array of major marker times
 */
function generateMajorMarkers(duration: number, interval: number): number[] {
  const markers: number[] = [];
  for (let i = 0; i <= duration; i += interval) {
    markers.push(i);
  }
  return markers;
}

/**
 * Generate array of minor marker times
 */
function generateMinorMarkers(duration: number, interval: number): number[] {
  const markers: number[] = [];
  const steps = Math.floor(duration / interval);
  for (let i = 0; i <= steps; i++) {
    const time = i * interval;
    // Round to avoid floating point precision issues
    markers.push(Math.round(time * 100) / 100);
  }
  return markers;
}

/**
 * Format time label based on value
 * - Under 60s: show seconds (e.g., "30")
 * - 60s to 3600s: show minutes:seconds (e.g., "2:30")
 * - Over 3600s: show hours:minutes (e.g., "1:30")
 */
function formatTimeLabel(seconds: number): string {
  if (seconds < 60) {
    // Show just seconds for values under 1 minute
    return seconds % 1 === 0 ? seconds.toString() : seconds.toFixed(1);
  } else if (seconds < 3600) {
    // Show M:SS format for values under 1 hour
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  } else {
    // Show H:MM format for values over 1 hour
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `${hours}:${mins.toString().padStart(2, '0')}`;
  }
}

/**
 * Calculate pixel position for a time marker
 */
function getMarkerPosition(time: number, pixelsPerSecond: number): number {
  return time * pixelsPerSecond;
}

export function TimelineRuler({ pixelsPerSecond = 100 }: TimelineRulerProps) {
  const duration = useTimelineStore((state) => state.duration);

  // Get tick spacing based on zoom level
  const { majorInterval, minorInterval } = useMemo(
    () => getTickSpacing(pixelsPerSecond),
    [pixelsPerSecond]
  );

  // Memoize marker generation to prevent recalculation on every render
  const majorMarkers = useMemo(
    () => generateMajorMarkers(duration, majorInterval),
    [duration, majorInterval]
  );
  const minorMarkers = useMemo(
    () => generateMinorMarkers(duration, minorInterval),
    [duration, minorInterval]
  );

  const rulerWidth = duration * pixelsPerSecond;

  return (
    <div
      className="relative h-8 bg-gray-800 border-b border-gray-700 select-none"
      style={{ width: `${rulerWidth}px`, minWidth: '100%' }}
      data-testid="timeline-ruler"
    >
      {/* Minor markers (grid lines) */}
      {minorMarkers.map((time) => {
        const position = getMarkerPosition(time, pixelsPerSecond);
        // Check if this time aligns with a major marker
        const isMajor = time % majorInterval === 0;

        return (
          <div
            key={`minor-${time}`}
            className="absolute top-0 h-full"
            style={{
              left: `${position}px`,
              width: '1px',
              backgroundColor: isMajor ? 'transparent' : '#404040',
            }}
          />
        );
      })}

      {/* Major markers (with labels) */}
      {majorMarkers.map((time) => {
        const position = getMarkerPosition(time, pixelsPerSecond);
        const label = formatTimeLabel(time);

        return (
          <div
            key={`major-${time}`}
            className="absolute top-0 h-full flex flex-col items-center"
            style={{ left: `${position}px` }}
          >
            {/* Vertical tick line */}
            <div className="w-px h-3 bg-gray-500" />

            {/* Time label */}
            <span className="text-[10px] text-gray-400 mt-0.5 font-mono">
              {label}
            </span>
          </div>
        );
      })}
    </div>
  );
}
